/* Racks — storage-location master + the medicine → home-shelf map.
 * Backed by v1/racks on the IMS service (httpSupplier). Envelope-aware.
 *
 * Two things live behind the same controller and are worth keeping apart:
 *   · the RACK master — the physical shelves in a store (code, name, status);
 *   · ASSIGNMENTS — which shelf is a medicine's home, used to prefill put-away.
 * A lot's actual placement is a third thing, written by the GRN (rackId per
 * item) and read back on the inventory rows — there is no endpoint to move a
 * lot on its own, so this service does not pretend there is. */
import { httpSupplier } from "../../HttpCommonALL";
import { getCurrentUserName } from "../../../Services/warehouseContext";

const BASE = "v1/racks";
/* Audit header — resolved per call so it reflects the currently signed-in user. */
const xUser = () => ({ "X-User": getCurrentUserName() });

/* Mirrors RackRequest's @Pattern / @Size on the backend, so a code the server
   would reject is refused before the round trip, with the reason shown. Lives here
   rather than in a component because both the picker and the create dialog need
   it, and importing it from one component into the other would make them
   circular. Returns null when the code is fine. */
export const rackCodeError = (raw) => {
  const code = String(raw || "").trim();
  if (!code) return "Enter a rack code.";
  if (code.length > 20) return "Rack code is at most 20 characters.";
  if (!/^[A-Za-z0-9][A-Za-z0-9 _/-]*$/.test(code)) {
    return "Letters, digits, space, hyphen, underscore and slash only.";
  }
  return null;
};

const unwrap = (r) => {
  const env = r?.data;
  if (env && typeof env === "object" && "success" in env) {
    if (env.success === false) throw new Error(env.message || "Request failed");
    return env.data;
  }
  return env;
};

/* Backend RackResponse -> the UI row shape the rack master renders. */
const fromRack = (r = {}) => ({
  id: r.rackId ?? null,
  warehouseId: r.warehouseId ?? "",
  code: r.code ?? "",
  name: r.name ?? "",
  description: r.description ?? "",
  /* How many medicines call this rack home, and how many lots are physically
     here. Both are counts the backend computes — never derived in the UI. */
  medicineCount: Number(r.assignedMedicineCount) || 0,
  lotCount: Number(r.lotCount) || 0,
  isActive: r.isActive !== false,
});

/* Backend RackAssignmentResponse -> UI assignment row. `locationLabel` is what
   staff read out loud ("R-01 · shelf 2, left"); it is composed server-side so
   every screen says it the same way. */
const fromAssignment = (a = {}) => ({
  id: a.assignmentId ?? null,
  warehouseId: a.warehouseId ?? "",
  medicineCode: a.medicineCode ?? "",
  medicineName: a.medicineName ?? "",
  rackId: a.rackId ?? null,
  rackCode: a.rackCode ?? "",
  rackName: a.rackName ?? "",
  rackActive: a.rackActive !== false,
  shelfPosition: a.shelfPosition ?? "",
  locationLabel: a.locationLabel ?? "",
});

/* Backend InventoryLotResponse -> the lot shape the rack drawer lists. Same
   field names supplyService's fromInventoryRow produces, so the drawer's table
   reads like every other batch table. */
const fromLot = (it = {}) => ({
  id: it.inventoryId,
  medId: it.medicineCode,
  medicineName: it.medicineName || "",
  batch: it.batchNo || "",
  exp: it.expiryDate || "",
  qtyA: Number(it.quantityAvailable) || 0,
  qtyR: Number(it.quantityReceived) || 0,
  mrp: Number(it.mrp) || 0,
  purchase: Number(it.purchasePrice) || 0,
  /* Same three fields fromInventoryRow carries, so a row patched from a move
     reads identically to one that came off the inventory list. */
  rackId: it.rackId ?? null,
  rackCode: it.rackCode || null,
  rackSource: it.rackSource || null,
});

/* RackRequest — `code` is required on update too, so a rename has to send it
   back unchanged. Blank optional fields are dropped rather than sent as "",
   which the backend's @Size checks would accept but store as empty strings. */
const toRackRequest = (form = {}) => {
  const trimmed = (v) => (v == null ? "" : String(v).trim());
  const name = trimmed(form.name);
  const description = trimmed(form.description);
  return {
    warehouseId: trimmed(form.warehouseId),
    code: trimmed(form.code),
    ...(name ? { name } : {}),
    ...(description ? { description } : {}),
    isActive: form.isActive !== false,
  };
};

const rackService = {
  /* GET v1/racks -> paged rack master. `search` matches code, name or
     description; `activeOnly` hides deactivated racks. */
  list({ warehouseId, activeOnly = false, search, page = 0, size = 20 } = {}) {
    const params = {
      ...(warehouseId ? { warehouseId } : {}),
      activeOnly,
      ...(search ? { search } : {}),
      page,
      size,
      sort: "code,asc",
    };
    return httpSupplier.get(BASE, { params, headers: xUser() }).then((r) => {
      const data = unwrap(r);
      const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
      return {
        content: content.map(fromRack),
        totalPages: data?.totalPages ?? (content.length ? 1 : 0),
        totalElements: data?.totalElements ?? content.length,
        page: data?.number ?? page,
        size: data?.size ?? size,
      };
    });
  },

  /* Every rack in a store, as a flat array — for the pickers, which need the
     whole (small) list rather than a page of it. 200 is well above any real
     store's shelf count; the pager on the master screen handles the rest. */
  listAll({ warehouseId, activeOnly = true } = {}) {
    return this.list({ warehouseId, activeOnly, size: 200 }).then((r) => r.content);
  },

  get(rackId) {
    return httpSupplier.get(`${BASE}/${rackId}`, { headers: xUser() }).then((r) => fromRack(unwrap(r)));
  },

  /* GET v1/racks/by-code -> the rack an operator typed, or null.
     A miss is 200 with data:null, NOT a 404 — so `null` here means "no such
     rack, offer to create it", and a thrown error means the call itself failed. */
  findByCode({ warehouseId, code }) {
    return httpSupplier
      .get(`${BASE}/by-code`, { params: { warehouseId, code }, headers: xUser() })
      .then((r) => {
        const data = unwrap(r);
        return data ? fromRack(data) : null;
      });
  },

  create(form) {
    return httpSupplier.post(BASE, toRackRequest(form), { headers: xUser() }).then((r) => fromRack(unwrap(r)));
  },

  update(rackId, form) {
    return httpSupplier
      .put(`${BASE}/${rackId}`, toRackRequest(form), { headers: xUser() })
      .then((r) => fromRack(unwrap(r)));
  },

  /* DELETE v1/racks/{id} — a soft delete (deactivate). Refused with 422 while
     the rack still holds stock or is some medicine's home, so the caller must
     surface the backend message rather than assume it worked. */
  deactivate(rackId) {
    return httpSupplier.delete(`${BASE}/${rackId}`, { headers: xUser() }).then((r) => unwrap(r));
  },

  /* Medicines whose home this rack is / what is physically sitting in it. */
  medicinesIn(rackId) {
    return httpSupplier
      .get(`${BASE}/${rackId}/medicines`, { headers: xUser() })
      .then((r) => (unwrap(r) || []).map(fromAssignment));
  },

  lotsIn(rackId) {
    return httpSupplier
      .get(`${BASE}/${rackId}/lots`, { headers: xUser() })
      .then((r) => (unwrap(r) || []).map(fromLot));
  },

  /* PATCH v1/inventory/lots/{id}/rack — put ONE batch in a rack, or take it out
     (rackId null). This is the only way to correct placement after inward; the
     rack on a GRN line writes it in the first place.

     The endpoint hangs off the inventory controller because it acts on a lot, but
     it is rack behaviour, so it lives here with the rest of it. Moves no stock —
     quantity, price and expiry come back unchanged. Returns the updated lot in the
     shape the stock tables use, so a caller can drop it straight into its row. */
  moveLot({ inventoryId, rackId, reason }) {
    const body = {
      rackId: rackId == null || rackId === "" ? null : Number(rackId),
      ...(reason ? { reason } : {}),
    };
    return httpSupplier
      .patch(`v1/inventory/lots/${inventoryId}/rack`, body, { headers: xUser() })
      .then((r) => fromLot(unwrap(r)));
  },

  /* --- medicine → home shelf ------------------------------------------ */

  /* Every medicine mapped in a warehouse. */
  assignments({ warehouseId }) {
    return httpSupplier
      .get(`${BASE}/assignments`, { params: { warehouseId }, headers: xUser() })
      .then((r) => (unwrap(r) || []).map(fromAssignment));
  },

  /* Medicines stocked here with no location yet — the rack-out worklist. */
  unassigned({ warehouseId }) {
    return httpSupplier
      .get(`${BASE}/unassigned`, { params: { warehouseId }, headers: xUser() })
      .then((r) =>
        (unwrap(r) || []).map((m) => ({ medicineCode: m.medicineCode, medicineName: m.medicineName || "" }))
      );
  },

  /* PUT v1/racks/assignments — upsert; re-sending moves the medicine. No
     warehouseId: rackId already determines the store. */
  assign({ medicineCode, rackId, medicineName, shelfPosition }) {
    const body = {
      medicineCode,
      rackId: Number(rackId),
      ...(medicineName ? { medicineName } : {}),
      ...(shelfPosition ? { shelfPosition } : {}),
    };
    return httpSupplier
      .put(`${BASE}/assignments`, body, { headers: xUser() })
      .then((r) => fromAssignment(unwrap(r)));
  },

  /* POST v1/racks/assignments/bulk — all-or-nothing, max 500 per call. One bad
     entry rejects the batch, so the caller keeps its edits until this resolves. */
  assignBulk(assignments = []) {
    const body = {
      assignments: assignments.map((a) => ({
        medicineCode: a.medicineCode,
        rackId: Number(a.rackId),
        ...(a.medicineName ? { medicineName: a.medicineName } : {}),
        ...(a.shelfPosition ? { shelfPosition: a.shelfPosition } : {}),
      })),
    };
    return httpSupplier
      .post(`${BASE}/assignments/bulk`, body, { headers: xUser() })
      .then((r) => (unwrap(r) || []).map(fromAssignment));
  },

  unassign({ warehouseId, medicineCode }) {
    return httpSupplier
      .delete(`${BASE}/assignments`, { params: { warehouseId, medicineCode }, headers: xUser() })
      .then((r) => unwrap(r));
  },

  /* GET v1/racks/lookup -> the home shelf for a set of medicines, in ONE call
     (a whole GRN or pick list). Codes with no location are simply absent from
     the response, so the result is returned as a Map keyed by medicine code and
     a caller can treat "missing" as "not racked yet".

     `medicineCodes` goes over the wire comma-joined: Spring binds a
     comma-separated string to List<String>, whereas axios's default array
     serialization (medicineCodes[]=…) would not bind at all. */
  async lookup({ warehouseId, medicineCodes = [] }) {
    const codes = [...new Set(medicineCodes.map((c) => String(c || "").trim()).filter(Boolean))];
    if (!warehouseId || !codes.length) return new Map();
    const r = await httpSupplier.get(`${BASE}/lookup`, {
      params: { warehouseId, medicineCodes: codes.join(",") },
      headers: xUser(),
    });
    const rows = (unwrap(r) || []).map(fromAssignment);
    return new Map(rows.map((a) => [String(a.medicineCode), a]));
  },
};

export default rackService;
